import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon, CloudDownload as CloudDownloadIcon, Save as SaveIcon } from '@mui/icons-material';
import { getJobResults, saveToTestSet, type ResultsResponse } from '../api/videoApi';

interface ResultsViewerComponentProps {
  jobId: string;
  isBenchmark?: boolean;
}

export default function ResultsViewerComponent({ jobId }: ResultsViewerComponentProps) {
  const [results, setResults] = useState<ResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const jobResults = await getJobResults(jobId);
        setResults(jobResults);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [jobId]);

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveToTestSet = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(null);
      const result = await saveToTestSet({ job_id: jobId });
      setSaveSuccess(`Test set saved: ${result.frame_count} frames, ${result.total_files} files`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save test set');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading results...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!results) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">No results available yet</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Processing Results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Download your processed video outputs
      </Typography>

      <List>
        {/* Camera Data JSON */}
        {results.camera_data && (
          <>
            <ListItem>
              <ListItemText
                primary="Camera Data (JSON)"
                secondary="Per-frame camera intrinsics and extrinsics"
              />
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(results.camera_data!, 'camera_data.json')}
              >
                Download
              </Button>
            </ListItem>
            <Divider />
          </>
        )}

        {/* EXR Depth Maps */}
        {results.depth_maps && results.depth_maps.length > 0 && (
          <>
            <ListItem>
              <ListItemText
                primary={`Depth Maps (EXR) - ${results.depth_maps.length} files`}
                secondary="Per-frame depth maps in EXR format"
              />
            </ListItem>
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              {results.depth_maps.map((depthMap, index) => (
                <ListItem key={index} dense>
                  <ListItemButton
                    onClick={() => handleDownload(depthMap.url, depthMap.filename)}
                  >
                    <ListItemText
                      primary={depthMap.filename}
                      secondary={`${Math.round((index + 1) / results.depth_maps.length * 100)}% of frames`}
                    />
                    <CloudDownloadIcon sx={{ ml: 2, color: 'action.active' }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </Box>
            <Divider />
          </>
        )}

        {!results.camera_data && results.depth_maps.length === 0 && (
          <ListItem>
            <Alert severity="info" sx={{ width: '100%' }}>
              No results available. The job may still be processing or no exports were selected.
            </Alert>
          </ListItem>
        )}
      </List>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Save all results to the permanent test set for future use. This may take up to a minute for large jobs.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={handleSaveToTestSet}
        disabled={saving}
        fullWidth
      >
        {saving ? 'Saving to Test Set...' : 'Save to Test Set'}
      </Button>
      {saveSuccess && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
    </Paper>
  );
}

